import { tr } from "./i18n.js";
/**
 * Pure helpers for Settings → Prompts backup (export / import / reset).
 * No React, no network — tested by frontend/scripts/promptBundle.test.mjs.
 */

export const MAX_FILE_BYTES = 2 * 1024 * 1024;

/** Parse an uploaded prompts file. Returns { bundle } or { error }. */
export function parsePromptFile(text, size = 0) {
  if (size > MAX_FILE_BYTES) {
    return { error: tr("That file is too large to be a prompts file (over 2 MB).") };
  }
  let data;
  try {
    data = JSON.parse(String(text ?? "").replace(/^﻿/, ""));
  } catch {
    return { error: tr("That file isn't valid JSON — pick a .json file exported from Newsdesk.") };
  }
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return { error: tr("That file isn't a Newsdesk prompts file.") };
  }
  if (!data.prompts || typeof data.prompts !== "object") {
    return { error: tr("That file has no \"prompts\" section.") };
  }
  return { bundle: data };
}

/** Counts per action, e.g. { changed: 3, added: 1, removed: 0, total: 4 }. */
export function countChanges(changes) {
  const out = { changed: 0, added: 0, removed: 0, total: 0 };
  for (const c of changes || []) {
    if (c && c.action in out) out[c.action] += 1;
    out.total += 1;
  }
  return out;
}

/** One-line summary for a toast or heading. */
export function describeChanges(changes) {
  const n = countChanges(changes);
  if (!n.total) return tr("No changes");
  const parts = [];
  if (n.changed) parts.push(`${n.changed} changed`);
  if (n.added) parts.push(`${n.added} added`);
  if (n.removed) parts.push(`${n.removed} removed`);
  return `${n.total} prompt${n.total === 1 ? "" : "s"} · ${parts.join(", ")}`;
}

/** Pretty JSON text for the downloaded file (stable, human-readable). */
export function bundleToText(bundle) {
  return JSON.stringify(bundle, null, 2) + "\n";
}

/** Trigger a browser download of `bundle` as `filename`. */
export function downloadBundle(filename, bundle) {
  const blob = new Blob([bundleToText(bundle)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "newsdesk-prompts.json";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
